import { useCallback, useMemo, useState } from 'react';
import { bomClient } from '../client/bomClient';
import { BomEditProposal } from '../types/bomProposal';
import { apiErrorMessage } from '../utils/apiErrorMessage';
import { assessProposal, changeCount, CurrentBomLine, toManualEdits } from '../utils/bomProposal';
import { useRecordRobbieAction } from '../contexts/RobbieUiContext';
import { robbieActionText } from '../utils/robbieActivity';

/**
 * One proposal card's state: which lines are ticked, applying them through
 * the manual edit (`source: 'robbie'`), and what became of it.
 *
 * The outcome — applied, nothing to change, dismissed — is kept per message
 * in `localStorage`, so a reload redraws the card as it was left rather than
 * offering an Apply that already happened. Storage is a convenience: when it
 * is unavailable the card simply opens again, and a re-apply comes back as
 * `noChanges` (said as such), never a second version.
 */

export type ProposalOutcome =
  | { status: 'open' }
  | { status: 'applied'; count: number; version: number | null }
  | { status: 'unchanged' }
  | { status: 'dismissed' };

export interface UseBomProposalOptions {
  /** Unique per proposal: the chat message id plus the action's position. */
  storageKey: string;
  proposal: BomEditProposal;
  /** The BOM the RFQ shows now (`analysisData.bomId`); null while unknown. */
  currentBomId: string | null;
  currentLines: CurrentBomLine[] | null;
  /** After a good apply — the caller re-reads the RFQ. */
  onApplied?: () => unknown;
}

const STORAGE_PREFIX = 'hr.robbie.bomProposal.';

const readOutcome = (key: string): ProposalOutcome => {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && ['applied', 'unchanged', 'dismissed'].includes(parsed.status)) return parsed;
  } catch {
    // Unreadable storage is the same as none.
  }
  return { status: 'open' };
};

const writeOutcome = (key: string, outcome: ProposalOutcome) => {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(outcome));
  } catch {
    // Private mode or a full quota: the card still shows the outcome now.
  }
};

const sentence = (text: string) => text.trim().replace(/[.\s]+$/, '');

export const useBomProposal = ({ storageKey, proposal, currentBomId, currentLines, onApplied }: UseBomProposalOptions) => {
  const [outcome, setOutcome] = useState<ProposalOutcome>(() => readOutcome(storageKey));
  const [unticked, setUnticked] = useState<Set<string>>(() => new Set());
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recordAction = useRecordRobbieAction();

  const assessment = useMemo(
    () => assessProposal(proposal, currentBomId, currentLines),
    [proposal, currentBomId, currentLines]
  );

  // Only a line that still holds what Robbie saw can be ticked; a line that
  // changed since drops out of the selection on its own.
  const selected = useMemo(
    () =>
      assessment.staleBom
        ? []
        : proposal.edits.filter((e) => assessment.lines[e.componentId] === 'fresh' && !unticked.has(e.componentId)),
    [assessment, proposal.edits, unticked]
  );

  const toggle = useCallback((componentId: string) => {
    setUnticked((prev) => {
      const next = new Set(prev);
      if (next.has(componentId)) next.delete(componentId);
      else next.add(componentId);
      return next;
    });
  }, []);

  const settle = useCallback(
    (next: ProposalOutcome) => {
      writeOutcome(storageKey, next);
      setOutcome(next);
    },
    [storageKey]
  );

  const apply = useCallback(async () => {
    if (selected.length === 0 || applying) return;
    setApplying(true);
    setError(null);
    try {
      const result = await bomClient.manualEditBom(proposal.bomId, {
        components: toManualEdits(selected),
        source: 'robbie',
      });
      settle(result.noChanges ? { status: 'unchanged' } : { status: 'applied', count: changeCount(selected), version: result.version });
      recordAction(
        'proposalApplied',
        result.noChanges
          ? robbieActionText.proposalUnchanged()
          : robbieActionText.appliedProposal(changeCount(selected), selected.length)
      );
      // The edit is saved; a refresh that fails must not read as a failed apply.
      Promise.resolve()
        .then(onApplied)
        .catch(() => undefined);
    } catch (err) {
      const reason = sentence(apiErrorMessage(err, "the server didn't say why"));
      setError(`I couldn't apply these, so the BOM is as it was: ${reason}. Try again, or make the change in the BOM sheet.`);
    } finally {
      setApplying(false);
    }
  }, [selected, applying, proposal.bomId, settle, onApplied, recordAction]);

  const dismiss = useCallback(() => {
    settle({ status: 'dismissed' });
    recordAction('proposalDismissed', robbieActionText.dismissedProposal());
  }, [settle, recordAction]);

  return {
    outcome,
    assessment,
    selected,
    selectedChanges: changeCount(selected),
    isTicked: (componentId: string) => selected.some((e) => e.componentId === componentId),
    toggle,
    applying,
    error,
    apply,
    dismiss,
  };
};

export type BomProposalState = ReturnType<typeof useBomProposal>;

export default useBomProposal;
